import { authHeaders } from './auth'
import * as samples from './samples'
import { DEMO } from '../demo'
import { sampleStatementBytes, sampleReconciliationBytes, saveBytes, sampleToast } from './sampleFiles'

const BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

/**
 * Thin fetch wrapper. Deliberately thin — hiding HTTP status codes behind an
 * abstraction makes a ledger problem harder to debug, not easier.
 *
 * Identity is attached here rather than at each call site, because a request
 * that forgets it does not fail loudly: it 401s, which reads like a sign-in
 * problem and sends somebody off to check a password that was never wrong.
 */
async function request(path, opts = {}) {
  // Sample records never reach the backend — see samples.js.
  if (samples.isSamplePath(path)) return sampleResponse(path, opts)
  let res
  try {
    res = await fetch(`${BASE}${path}`, {
      credentials: 'include',
      ...opts,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...opts.headers },
      // Reading a scanned statement genuinely takes a while, so this is
      // generous — but not unbounded, or a wedged call leaves a spinner up
      // with nothing to tell the user.
      signal: AbortSignal.timeout(opts.timeoutMs ?? 120000),
    })
  } catch (e) {
    throw new Error(e?.name === 'TimeoutError' ? 'That took too long. Try again.' : 'Could not reach the server.')
  }

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    /*
     * 401 means the session itself is no longer valid — sign out, since every
     * other request will fail the same way.
     *
     * **403 deliberately does not.** That means this particular request was not
     * permitted, not that the session is bad. PCC learned this the expensive
     * way: treating 403 as a logout signed out every non-admin the moment a
     * screen touched an admin-only endpoint on load.
     *
     * A window event rather than a callback, so nothing has to be registered
     * before the first request can be made.
     */
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))

    // 503 is the backend saying it cannot check a sign-in at all — a missing
    // secret, or Vision unreachable. Its message names which, and passing it
    // through is the difference between a five-minute fix and an afternoon.
    // The server writes these for a person to read — pass them through rather
    // than replacing them with a generic failure message.
    // The status and code ride along, so a caller can tell "not allowed in" apart from a failure.
    throw Object.assign(new Error(body?.message || `Something went wrong (${res.status}).`), { status: res.status, code: body?.code ?? null })
  }
  return body?.data
}

/**
 * A sample record's reads, answered locally; every write refused. Only
 * called for a path naming a sample id or code, so a real record can never
 * land here.
 */
function sampleResponse(path, opts) {
  const method = (opts.method ?? 'GET').toUpperCase()
  const clean = path.split('?')[0]
  let m
  if (method === 'GET') {
    if ((m = clean.match(/^\/ledger\/requests\/([^/]+)\/activity$/))) {
      const out = samples.sampleActivity(m[1])
      if (out) return Promise.resolve(out)
    } else if ((m = clean.match(/^\/ledger\/requests\/([^/]+)$/))) {
      const out = samples.sampleRequestDetail(m[1])
      if (out) return Promise.resolve(out)
    } else if ((m = clean.match(/^\/ledger\/clients\/([^/]+)\/documents$/))) {
      return Promise.resolve(samples.sampleClientDocuments(decodeURIComponent(m[1])))
    } else if ((m = clean.match(/^\/remediation\/(?:items|mine)\/([^/]+)$/))) {
      const out = samples.sampleRemediationItem(m[1])
      if (out) return Promise.resolve(out)
    }
  }
  // Marking a sample thread read is harmless to skip silently.
  if (/\/read$/.test(clean)) return Promise.resolve(null)
  // A sample fix's start/pause/resume/done changes the sample itself, so the
  // screen moves on like it would for a real one.
  if ((m = clean.match(/^\/remediation\/items\/([^/]+)\/(start|pause|resume|mark-done)$/))) {
    const note = (() => { try { return JSON.parse(opts.body ?? '{}').note ?? null } catch { return null } })()
    const out = samples.sampleRemediationAct(m[1], m[2], note)
    if (out?.blockedBy) return Promise.reject(new Error(`You're already working on ${out.blockedBy} — pause it or mark it done first.`))
    if (out) {
      sampleToast(`Sample fix — ${{ start: 'started', pause: 'paused', resume: 'resumed', 'mark-done': 'marked done' }[m[2]]} (simulated, nothing saved).`)
      return Promise.resolve(out)
    }
  }
  // Every other write on a sample: simulated — it "works", nothing leaves the browser.
  if (method !== 'GET') {
    sampleToast()
    return Promise.resolve({ ok: true, simulated: true })
  }
  return Promise.reject(new Error(samples.SAMPLE_REFUSAL))
}

/** Is this one of the samples' made-up numbers (+91 98000 000xx)? */
const isSampleNumber = (e164) => /^\+919800000\d{3}$/.test(String(e164 ?? ''))

/** Sample codes are dropped from anything sent to the backend in bulk. */
// In the demo every client is a sample, and the demo's own fetch keeps them in memory.
const realCodes = (codes) => (DEMO ? codes ?? [] : (codes ?? []).filter((c) => !samples.isSampleCode(c)))

const periodLabelOf = (to) =>
  `${new Date(`${to}T00:00:00Z`).toLocaleString('en-IN', { month: 'short', timeZone: 'UTC' })}-${to.slice(0, 4)}`

export const listRequests = async ({ from, to, status }) => {
  const data = await request(`/ledger/requests?from=${from}&to=${to}${status ? `&status=${status}` : ''}`)
  if (!samples.SHOW_SAMPLES) return data
  const extra = samples.sampleRequests({ from, to, label: periodLabelOf(to) }).filter((r) => !status || r.status === status)
  const requests = [...(data?.requests ?? []), ...extra]
  const summary = { ...(data?.summary ?? {}) }
  for (const [k, v] of Object.entries(samples.sampleSummary(extra))) summary[k] = (summary[k] ?? 0) + v
  return { ...data, requests, summary }
}

/**
 * Everything our own `clients` collection knows that the Track ledger does not:
 * which party codes to leave out, and a WhatsApp number and email for the ones
 * kept, all keyed by code.
 *
 * One call rather than two — it used to be `listExcludedClients` and
 * `listClientContacts` separately, fetched together on every page load for
 * numbers computed from the same rows. The list itself still comes from Track,
 * which carries a party's `Category` but not its `SubCategory` — this is what
 * the screen joins it against.
 */
export const listClientScreenData = async () => {
  const data = await request('/ledger/clients/screen-data')
  if (!samples.SHOW_SAMPLES) return data
  const extra = samples.sampleScreenData()
  return {
    ...data,
    contacts: { ...(data?.contacts ?? {}), ...extra.contacts },
    // The demo keeps its own list (seeded from the samples), so it can shrink too.
    autoChaseCodes: DEMO ? data?.autoChaseCodes ?? [] : [...(data?.autoChaseCodes ?? []), ...extra.autoChaseCodes],
  }
}

/** Add or remove a batch of clients from the list the daily scheduled chase reads. */
export const setAutoChase = (clientCodes, enabled) => {
  const codes = realCodes(clientCodes)
  if (!codes.length) return Promise.resolve({ matched: 0 })
  return request('/ledger/clients/auto-chase', {
    method: 'POST',
    body: JSON.stringify({ clientCodes: codes, enabled }),
  })
}

export const getRequest = (id) => request(`/ledger/requests/${id}`)

export const openPeriod = ({ from, to, label, clientCodes }) => {
  const codes = clientCodes === undefined ? undefined : realCodes(clientCodes)
  if (codes && !codes.length) return Promise.resolve(null)
  return request('/ledger/requests/open', {
    method: 'POST',
    body: JSON.stringify({ from, to, label, clientCodes: codes }),
  })
}

/**
 * Our chases and the system's own steps, on one timeline, newest first.
 *
 * Inbound WhatsApp arrives too, but this screen no longer shows any of it —
 * see ActivityDrawer.jsx. What a client's message meant is read automatically
 * on the backend or resolved by a tap; nothing here reads it for display.
 */
export const getActivity = (id) => request(`/ledger/requests/${id}/activity`)

/**
 * Tell the backend a person has opened this thread — clears its unread badge.
 *
 * Chat-app semantics: opening the drawer marks the messages read, so the badge
 * drops even before you reply. Separate from getActivity on purpose — merely
 * loading the timeline is not the same as a person reading it.
 */
export const markThreadRead = (id) => request(`/ledger/requests/${id}/read`, { method: 'POST' })

/**
 * Download a client's attachment through the backend proxy.
 *
 * Not a plain <a href>: the endpoint is auth-gated by the x-user-id header, which
 * a link cannot carry, and the file comes back as an attachment stream. So we
 * fetch it with the session headers, turn it into a blob, and save it — the only
 * way a header-authenticated download works in the browser.
 */
/** Download a request's received statement file (the list-screen View). */
export async function downloadStatement(id, fileName) {
  if (samples.isSampleId(id)) return saveBytes(sampleStatementBytes(fileName), `${fileName || 'sample-statement'}.pdf`, 'application/pdf')
  const res = await fetch(`${BASE}/ledger/requests/${id}/statement`, {
    credentials: 'include',
    headers: authHeaders(),
  })
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))
    const body = await res.json().catch(() => null)
    throw new Error(body?.message || `Could not open the statement (${res.status}).`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'statement'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Pull "our side" of the ledger fresh from Gati — rows and balance. The retry action for a row whose
 * last attempt failed: `request()` throws the same readable message a plain
 * page reload already gets from `ourSide.lastError`, so a caller showing
 * one can show the other with no new error-handling of its own.
 */
export const pullOurRecords = (id) => request(`/ledger/requests/${id}/pull-ours`, { method: 'POST' })

/** Shared by the ledgers drawer's per-file and zip downloads — bytes, not a save. */
async function fetchProxyBytes(path, notFoundMessage) {
  if (samples.isSamplePath(path)) return sampleStatementBytes()
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', headers: authHeaders() })
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))
    const body = await res.json().catch(() => null)
    throw new Error(body?.message || `${notFoundMessage} (${res.status}).`)
  }
  return new Uint8Array(await res.arrayBuffer())
}

/** Their statement's bytes, for the ledgers drawer (single file or zipped). */
export const fetchStatementBytes = (id) =>
  fetchProxyBytes(`/ledger/requests/${id}/statement`, 'Could not open the statement')

/**
 * Hand in a client's statement ourselves — for one that reached us outside
 * WhatsApp and email (in person, a personal inbox, over a call). Counts as
 * received and confirmed: the chase stops and it goes to reconciliation.
 * Raw body, so the stored hash is of the exact bytes.
 */
export async function uploadStatement(requestId, file) {
  if (samples.isSampleId(requestId)) {
    sampleToast(`Sample — "${file?.name ?? 'file'}" not uploaded (simulated).`)
    return { simulated: true }
  }
  const res = await fetch(`${BASE}/ledger/requests/${requestId}/upload`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeaders(), 'Content-Type': file.type || 'application/octet-stream', 'x-filename': file.name },
    body: file,
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))
    throw new Error(body?.message || `Upload failed (${res.status}).`)
  }
  return body?.data
}

/** One client's collected ledgers, one folder per cycle — the ledgers drawer. */
export function listClientDocuments(clientCode, { from = '', to = '', sortBy = 'newest', page = 1, limit = 5 } = {}) {
  const q = new URLSearchParams({ sortBy, page: String(page), limit: String(limit) })
  if (from) q.set('from', from)
  if (to) q.set('to', to)
  return request(`/ledger/clients/${encodeURIComponent(clientCode)}/documents?${q}`)
}

export async function downloadMedia(id, providerMsgId, fileName) {
  if (samples.isSampleId(id)) return saveBytes(sampleStatementBytes(fileName), fileName || 'sample-attachment.pdf', 'application/pdf')
  const res = await fetch(`${BASE}/ledger/requests/${id}/media/${encodeURIComponent(providerMsgId)}`, {
    credentials: 'include',
    headers: authHeaders(),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))
    throw new Error(body?.message || `Could not download the file (${res.status}).`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'attachment'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoke on the next tick — revoking synchronously can cancel the download in
  // some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Send one chase on WhatsApp. The server picks session text or template. */
export const sendWhatsApp = (id, { templateKey, promisedFor = null }) =>
  request(`/ledger/requests/${id}/whatsapp`, {
    method: 'POST',
    body: JSON.stringify({ templateKey, promisedFor }),
  })

/**
 * Correct a client's contact by hand — for a bounced number, where there is
 * no client message to classify a redirect off of. Resets the chase cycle
 * the same way a classified redirect already does.
 */
export const redirectContact = (id, { name, whatsAppNo = null, email = null, note = null }) =>
  request(`/ledger/requests/${id}/redirect-contact`, {
    method: 'POST',
    body: JSON.stringify({ name, whatsAppNo, email, note }),
  })

/**
 * A link to open this number's chat in DoubleTick's own console.
 *
 * Resolved fresh on every call — DoubleTick's contact id for a number is
 * never stored here, only looked up on demand. A number DoubleTick has no
 * contact for yet 404s, which is ordinary, not a fault.
 */
export const getDoubleTickLink = (e164) =>
  isSampleNumber(e164) ? (sampleToast('Sample number — no DoubleTick chat to open.'), Promise.reject(new Error('Sample number — no chat to open.'))) :
  request(`/ledger/whatsapp/doubletick-link?phone=${encodeURIComponent(e164)}`)

/* ─── Test bench ─────────────────────────────────────────────────────────────
 *
 * Driving the real flow against a number somebody owns. Sending is not here on
 * purpose — that is `sendWhatsApp` above, the same call the collection screen
 * makes. A bench with its own send path would prove only that the bench works.
 */





/* ─── Ledger reconciler (test bench) ─────────────────────────────────────────
 *
 * Every reconciliation goes through the Batch API — roughly half the token
 * cost of a synchronous call, at the price of getting a `jobId` back instead
 * of an answer. Its own request path, not `request()` above — that helper
 * always sends JSON, and two uploaded files need multipart instead.
 */

/** History — every past reconciliation run, most recent first, read straight
 *  from the database rather than a live call. */
export const listReconciliationRuns = async ({ limit = 50 } = {}) => {
  const rows = await request(`/ledger-reconcile/runs?limit=${limit}`)
  if (!samples.SHOW_SAMPLES) return rows
  return [...(rows ?? []), ...samples.sampleRunHistory()].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
}

/** Every client's collection cycles and where each one's reconciliation
 *  stands — the Reconciler page's tracker. `{ rows }`, one per cycle, each
 *  with a backend-decided `stage`. */
export const getReconciliationTracker = async () => {
  const data = await request('/ledger-reconcile/tracker')
  if (!samples.SHOW_SAMPLES) return data
  return { ...data, rows: [...(data?.rows ?? []), ...samples.sampleTrackerRows()] }
}

/**
 * The latest reconciliation run for each of these LedgerRequest ids, one
 * call — what the Ledger Collector's own "post-reco chase" filter reads to
 * tell a request still being collected apart from one that's already been
 * reconciled (and, if it found client-side issues, needs a follow-up
 * chase). Empty ids array short-circuits to an empty result without a
 * round trip.
 */
export const latestReconciliationRuns = async (ledgerRequestIds) => {
  const ids = ledgerRequestIds.filter((id) => !samples.isSampleId(id))
  const extra = samples.SHOW_SAMPLES ? samples.sampleLatestRuns() : []
  if (ids.length === 0) return { runs: extra }
  const data = await request(`/ledger-reconcile/runs/latest-by-request?ledgerRequestIds=${ids.join(',')}`)
  return { ...data, runs: [...(data?.runs ?? []), ...extra] }
}

/**
 * Submit one or more ours/client pairs together as one real Anthropic
 * batch. `sets` is `[{oursFile, clientFile}, ...]` — repeated `ours`/
 * `client` form fields, in order, so the backend can pair them back up by
 * index (`req.files.ours[i]` with `req.files.client[i]`).
 *
 * Returns `{batchId, results}` — `results` has one entry per input set, in
 * order: `{ok: true, jobId}` once genuinely submitted (poll it with
 * `getReconciliationBatchStatus`), or `{ok: false, reason, message}` for a
 * set that failed a $0 gate before ever reaching Anthropic. One bad set
 * never blocks the others. Submission itself is a normal-speed request (a
 * Files API upload per side, per set, plus one batch create), so a much
 * shorter timeout than a full reconciliation would need.
 */
export async function submitReconciliationBatch({ sets, signal }) {
  const form = new FormData()
  for (const s of sets) {
    form.append('ours', s.oursFile)
    form.append('client', s.clientFile)
  }

  const timeoutSignal = AbortSignal.timeout(60000)
  const combinedSignal = signal ? AbortSignal.any([timeoutSignal, signal]) : timeoutSignal

  let res
  try {
    res = await fetch(`${BASE}/ledger-reconcile/batches`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(), // no Content-Type — the browser sets the multipart boundary
      body: form,
      signal: combinedSignal,
    })
  } catch (e) {
    if (e?.name === 'TimeoutError') throw new Error('That took too long. Try again.')
    if (signal?.aborted) throw new DOMException('Cancelled by user.', 'AbortError')
    throw new Error('Could not reach the server.')
  }

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))
    throw new Error(body?.message || `Something went wrong (${res.status}).`)
  }
  return body?.data
}

/** Read one submitted batch's status — `{jobId, processingStatus, finalized}`
 *  while still pending, plus `{outcome, runId, status, failureReason,
 *  excelFilename}` once `finalized` is true. A plain read — see
 *  `checkReconciliationBatch` for the one that can actually move it along. */
export const getReconciliationBatchStatus = (jobId) => request(`/ledger-reconcile/batches/${jobId}`)

/** "Check status" — actively checks this batch against Anthropic and
 *  finalizes it if it's ended, then returns the same shape
 *  `getReconciliationBatchStatus` does. There's no external scheduler
 *  hitting the backend's poll job periodically, so this button press is
 *  what actually drives a batch to completion in practice. */
export const checkReconciliationBatch = (jobId) =>
  request(`/ledger-reconcile/batches/${jobId}/check`, { method: 'POST' })

/** Best-effort cancel for a pending batch — see the backend's own
 *  `cancelReconciliationBatch` for why this can't promise the spend stops
 *  the instant it's called; a batch may finish any in-progress request
 *  before actually cancelling. */
export const cancelReconciliationBatch = (jobId) =>
  request(`/ledger-reconcile/batches/${jobId}/cancel`, { method: 'POST' })

/* ─── Accountant Remediation ─────────────────────────────────────────────────
 *
 * Internal Cliq nudging of Binny's own accountants about our-side ledger
 * discrepancies. The admin screen is read-only — items get raised and
 * routed automatically (fixed category → accountant, no manual assign); see
 * the backend's remediation.routes.js for the write endpoints this
 * deliberately doesn't call. Confirmation itself is NOT read-only, though —
 * it's the accountant's own "My Ledgers" action below.
 */

/** The list, newest first — optionally narrowed to one status for the
 *  screen's tabs, or one client. */
export const listRemediationItems = ({ status = null, clientCode = null } = {}) => {
  const q = new URLSearchParams()
  if (status && status !== 'all') q.set('status', status)
  if (clientCode) q.set('clientCode', clientCode)
  const qs = q.toString()
  return request(`/remediation/items${qs ? `?${qs}` : ''}`).then((data) => {
    if (!samples.SHOW_SAMPLES) return data
    const extra = samples
      .sampleRemediationItems()
      .filter((i) => (!status || status === 'all' || i.status === status) && (!clientCode || i.clientCode === clientCode))
    return { ...data, items: [...(data?.items ?? []), ...extra] }
  })
}

/** One item, for the detail drawer. */
export const getRemediationItem = (id) => request(`/remediation/items/${id}`)

/** Admin marks an accountant away / back — their not-started fixes move to
 *  their backup, and come back on return if the backup hasn't opened them. */
export const setAccountantAway = (email) =>
  request(`/remediation/accountants/${encodeURIComponent(email)}/away`, { method: 'POST', body: '{}' })
export const setAccountantBack = (email) =>
  request(`/remediation/accountants/${encodeURIComponent(email)}/back`, { method: 'POST', body: '{}' })

/** The accountant directory — the admin tracker uses it to show a name
 *  rather than an email in the "Assigned to" column. Admin-only. */
export const listAccountants = () => request('/remediation/accountants')

/* ─── Accounts Team (admin) ───────────────────────────────────────────────── */

/** Everyone on the team, deactivated people included. */
export const listTeam = () => request('/remediation/accountants?all=1')
export const addAccountant = (body) => request('/remediation/accountants', { method: 'POST', body: JSON.stringify(body) })
export const updateAccountant = (email, body) =>
  request(`/remediation/accountants/${encodeURIComponent(email)}`, { method: 'PATCH', body: JSON.stringify(body) })
export const deactivateAccountant = (email, { moveTo = null } = {}) =>
  request(`/remediation/accountants/${encodeURIComponent(email)}/deactivate`, { method: 'POST', body: JSON.stringify({ moveTo }) })
export const reactivateAccountant = (email) =>
  request(`/remediation/accountants/${encodeURIComponent(email)}/reactivate`, { method: 'POST', body: '{}' })

/**
 * What the signed-in person gets to see: `{ username, isAdmin, accountant }`.
 * Asked once after sign-in — the session itself comes from Vision, which
 * knows nothing about lereco's admin side. Drives the nav only; every admin
 * API is refused server-side for a non-admin regardless.
 */
export const getMe = () => request('/me')

/** "My Ledgers" — whatever's open and assigned to the signed-in user, read
 *  off their own email server-side. Empty for anyone not in the accountant
 *  directory. */
export const listMyRemediationItems = async ({ status = 'open' } = {}) => {
  const data = await request(`/remediation/mine${status === 'done' ? '?status=done' : ''}`)
  if (!samples.SHOW_SAMPLES) return data
  return { ...data, items: [...(data?.items ?? []), ...samples.sampleMyItems(null, { done: status === 'done' })] }
}

/** One of my own items, done or not — the My Ledgers detail page. */
export const getMyRemediationItem = (id) => request(`/remediation/mine/${id}`)

/** Start / pause / resume — the accountant's own progress on one of their
 *  items, from My Ledgers. Same server-side ownership check as mark-done;
 *  `note` on pause is the reason, shown to admins. */
export const startRemediation = (id) => request(`/remediation/items/${id}/start`, { method: 'POST', body: '{}' })
export const pauseRemediation = (id, { note = null } = {}) =>
  request(`/remediation/items/${id}/pause`, { method: 'POST', body: JSON.stringify({ note }) })
export const resumeRemediation = (id) => request(`/remediation/items/${id}/resume`, { method: 'POST', body: '{}' })

/** The accountant marks their own item done — the real action that
 *  replaced the old Cliq-reply confirm. Authorized server-side against the
 *  signed-in user's own email, not anything this call claims. */
export const markRemediationDone = (id, { note = null } = {}) =>
  request(`/remediation/items/${id}/mark-done`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  })

/** Download one run's Excel deliverable — live, right after it finishes, or
 *  any time later from History. Same proxy shape as `downloadStatement`
 *  above: the bytes live in S3, not on this server, and the endpoint is
 *  auth-gated by a header a plain `<a href>` can't carry. */
export async function downloadReconciliationExcel(runId, fileName) {
  if (samples.isSampleId(runId)) {
    return saveBytes(sampleReconciliationBytes(), fileName || 'sample-reconciliation.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
  }
  const res = await fetch(`${BASE}/ledger-reconcile/runs/${runId}/excel`, {
    credentials: 'include',
    headers: authHeaders(),
  })
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event('lereco:unauthorized'))
    const body = await res.json().catch(() => null)
    throw new Error(body?.message || `Could not download the Excel (${res.status}).`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName || 'reconciliation.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Everything tracked, for the admin Reports screen — rows per ledger cycle,
 * every internal fix, the accountant directory and 90 days of activity (see
 * reports.service.js). Samples ride along like everywhere else.
 */
export const getReports = async () => {
  const data = await request('/reports')
  if (!samples.SHOW_SAMPLES) return data
  return {
    ...data,
    ledgers: [...(data?.ledgers ?? []), ...samples.sampleReportLedgers()],
    items: [...(data?.items ?? []), ...samples.sampleReportItems()],
  }
}
